import React from 'react';
import { observer } from 'mobx-react';
import { Pagination } from 'ats/components/pagination/Pagination';
import { ListStore } from 'shared/stores/ListStore';
import { formatTimeAgo } from 'shared/utils';
import { JobApplicationHistoryStore } from 'ats/pages/pipeline/components/JobApplicationHistoryStore';
import { jobPositionStore } from 'ats/pages/job-positions/JobPositionStore';
import { openJobPositionProfile } from 'ats/utils/openEntityProfile';
import JobApplicationHistoryCard from './JobApplicationHistoryCard';
import JobApplicationTaskCard from './JobApplicationTaskCard';

const getEntryKey = entry => `${entry.source}-${entry.id}`;

const groupConsecutiveEntriesByJobApplication = entries =>
	entries.reduce((groups, entry) => {
		const lastGroup = groups[groups.length - 1];
		if (lastGroup && lastGroup.jobApplicationId === entry.jobApplicationId) {
			lastGroup.entries.push(entry);
		} else {
			groups.push({
				key: `${entry.jobApplicationId}-${getEntryKey(entry)}`,
				jobApplicationId: entry.jobApplicationId,
				entries: [entry]
			});
		}
		return groups;
	}, []);

@observer
export class ApplicantHistoryTimeline extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			collapsedGroupKeys: new Set()
		};
		this.jobApplicationHistoryStore = new JobApplicationHistoryStore();
		this.listStore = new ListStore(this.jobApplicationHistoryStore, {
			entityName: 'Job Application History'
		});
	}

	preConfigureQuery() {
		this.listStore.store.resetQuery();
		this.listStore.store.setQuery('applicantId', this.props.applicant.id);
		this.listStore.store.setQuery('sort', {
			column: 'createdAt',
			asc: false
		});
	}

	componentDidMount() {
		this.preConfigureQuery();
		this.listStore.search();
	}

	componentDidUpdate(prevProps) {
		if (prevProps.applicant?.id !== this.props.applicant?.id) {
			this.preConfigureQuery();
			this.setState({ collapsedGroupKeys: new Set() });
			this.listStore.search();
		} else if (prevProps.applicant !== this.props.applicant) {
			this.listStore.search();
		}
	}

	handlePaginationChange = value => {
		this.listStore.handlePaginationChange(
			value,
			this.listStore.store.query.pagination.limit
		);
	};

	getJobApplication = jobApplicationId =>
		this.props.applicant?.jobApplications?.find(
			jobApplication => jobApplication.id === jobApplicationId
		);

	getJobStatus = value =>
		jobPositionStore.statusOptions.find(status => status.value === value);

	handleJobTitleClick = jobId => {
		openJobPositionProfile(jobId, {
			onSuccess: () => {
				this.props.onSuccess?.();
				this.listStore.search();
			}
		});
	};

	toggleGroup = groupKey => {
		this.setState(({ collapsedGroupKeys }) => {
			const nextKeys = new Set(collapsedGroupKeys);
			if (nextKeys.has(groupKey)) {
				nextKeys.delete(groupKey);
			} else {
				nextKeys.add(groupKey);
			}
			return { collapsedGroupKeys: nextKeys };
		});
	};

	toggleAllGroups = (groups, collapse) => {
		this.setState({
			collapsedGroupKeys: collapse
				? new Set(groups.map(group => group.key))
				: new Set()
		});
	};

	renderEntry = entry =>
		entry.source === 'task' ? (
			<JobApplicationTaskCard
				key={getEntryKey(entry)}
				task={entry}
				onSuccess={() => this.listStore.search()}
			/>
		) : (
			<JobApplicationHistoryCard
				key={getEntryKey(entry)}
				history={entry}
				onSuccess={() => this.listStore.search()}
			/>
		);

	renderGroup = (group, index, groups) => {
		const jobApplication = this.getJobApplication(group.jobApplicationId);
		const job = jobApplication?.job;
		const jobStatusData = this.getJobStatus(job?.status);
		const isCollapsed = this.state.collapsedGroupKeys.has(group.key);
		const areAllCollapsed = groups.every(item =>
			this.state.collapsedGroupKeys.has(item.key)
		);
		const latestEntry = group.entries[0];

		return (
			<div className="applicant-history-timeline-group" key={group.key}>
				<div className="applicant-history-timeline-group-header">
					<div className="applicant-history-timeline-group-title">
						<h3
							onClick={() =>
								this.handleJobTitleClick(job?.id ?? latestEntry.jobId)
							}>
							<span className="job-position-id">
								#{job?.id ?? latestEntry.jobId}
							</span>
							{job?.title}
						</h3>
						{job?.client?.name && (
							<span className="job-client">{job.client.name}</span>
						)}
						{jobStatusData && (
							<span
								className="job-status"
								style={{ color: jobStatusData.colors?.text }}>
								{jobStatusData.text}
							</span>
						)}
					</div>
					<div className="applicant-history-timeline-group-details">
						{index === 0 && (
							<button
								type="button"
								className="applicant-history-timeline-collapse-all"
								onClick={() => this.toggleAllGroups(groups, !areAllCollapsed)}>
								{areAllCollapsed ? 'Expand all' : 'Collapse all'}
							</button>
						)}
						<div className="date">
							{formatTimeAgo(new Date(latestEntry.updatedAt), {
								moreThanOneDay: true
							})}
						</div>
						<div className="toggle-button">
							<button
								type="button"
								aria-expanded={!isCollapsed}
								aria-label={
									isCollapsed ? 'Expand position' : 'Collapse position'
								}
								onClick={() => this.toggleGroup(group.key)}>
								<img
									src={`/ats/icons/${
										isCollapsed
											? 'chevron-down-icon.svg'
											: 'chevron-up-icon.svg'
									}`}
									alt=""
								/>
							</button>
						</div>
					</div>
				</div>
				{!isCollapsed && (
					<div className="job-application-history">
						<div className="job-application-history-list">
							{group.entries.map(this.renderEntry)}
						</div>
					</div>
				)}
			</div>
		);
	};

	render() {
		const groups = groupConsecutiveEntriesByJobApplication(this.listStore.data);

		if (groups.length === 0) {
			return null;
		}

		return (
			<div className="applicant-history-timeline">
				<div className="applicant-history-timeline-list">
					{groups.map(this.renderGroup)}
				</div>
				<Pagination
					loading={this.listStore.loading}
					pagination={this.listStore.store.query.pagination}
					onPaginationChange={this.handlePaginationChange}
				/>
			</div>
		);
	}
}
